/**
 * New Header Script - Clean and Modular JavaScript
 * Handles desktop and mobile navigation separately
 */

class NewHeaderNavigation {
  constructor() {
    this.isMobile = window.innerWidth < 768;
    this.mobileDrawer = null;
    this.hamburgerBtn = null;
    this.closeBtn = null;
    this.overlay = null;
    
    this.init();
  }

  init() {
    this.setupMobileNavigation();
    this.setupDesktopNavigation();
    this.setupEventListeners();
    this.setupResponsiveHandling();
  }

  setupMobileNavigation() {
    this.mobileDrawer = document.getElementById('mobileNavDrawer');
    this.hamburgerBtn = document.querySelector('.mobile-hamburger-btn');
    this.closeBtn = document.querySelector('.mobile-drawer-close');
    this.overlay = document.querySelector('.mobile-drawer-overlay');

    if (!this.mobileDrawer || !this.hamburgerBtn) return;

    // Mobile menu triggers
    const mobileTriggers = document.querySelectorAll('.mobile-click-trigger');
    mobileTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleMobileSubmenu(trigger);
      });
    });
  }

  setupDesktopNavigation() {
    if (this.isMobile) return;

    // Desktop hover triggers
    const desktopTriggers = document.querySelectorAll('.desktop-hover-trigger');
    desktopTriggers.forEach(trigger => {
      trigger.addEventListener('mouseenter', () => {
        this.showDesktopSubmenu(trigger);
      });

      trigger.addEventListener('mouseleave', () => {
        this.hideDesktopSubmenu(trigger);
      });
    });
  }

  setupEventListeners() {
    // Hamburger button
    if (this.hamburgerBtn) {
      this.hamburgerBtn.addEventListener('click', () => {
        this.toggleMobileDrawer();
      });
    }

    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => {
        this.closeMobileDrawer();
      });
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeMobileDrawer();
      });
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.mobileDrawer?.classList.contains('active')) {
        this.closeMobileDrawer();
      }
    });

    // Search functionality
    const searchBtn = document.querySelector('.search-button');
    if (searchBtn) {
      searchBtn.addEventListener('click', () => {
        this.handleSearch();
      });
    }

    // GA tracking for menu items
    this.setupGATracking();
  }

  setupResponsiveHandling() {
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 768;

      // If switching from mobile to desktop, close mobile drawer
      if (wasMobile && !this.isMobile && this.mobileDrawer?.classList.contains('active')) {
        this.closeMobileDrawer();
      }
    });
  }

  toggleMobileDrawer() {
    if (!this.mobileDrawer) return;

    const isActive = this.mobileDrawer.classList.contains('active');
    
    if (isActive) {
      this.closeMobileDrawer();
    } else {
      this.openMobileDrawer();
    }
  }

  openMobileDrawer() {
    if (!this.mobileDrawer || !this.hamburgerBtn) return;

    this.mobileDrawer.classList.add('active');
    this.hamburgerBtn.classList.add('active');
    document.body.style.overflow = 'hidden';
    
    // Focus management
    const firstFocusable = this.mobileDrawer.querySelector('button, a, input');
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  closeMobileDrawer() {
    if (!this.mobileDrawer || !this.hamburgerBtn) return;

    this.mobileDrawer.classList.remove('active');
    this.hamburgerBtn.classList.remove('active');
    document.body.style.overflow = '';
    
    // Close all submenus
    const activeTriggers = document.querySelectorAll('.mobile-click-trigger.active');
    activeTriggers.forEach(trigger => {
      trigger.classList.remove('active');
    });
  }

  toggleMobileSubmenu(trigger) {
    const isActive = trigger.classList.contains('active');
    
    // Close all other submenus
    const allTriggers = document.querySelectorAll('.mobile-click-trigger');
    allTriggers.forEach(t => {
      if (t !== trigger) {
        t.classList.remove('active');
      }
    });

    // Toggle current submenu
    if (isActive) {
      trigger.classList.remove('active');
    } else {
      trigger.classList.add('active');
    }
  }

  showDesktopSubmenu(trigger) {
    // Hide all other submenus
    const allTriggers = document.querySelectorAll('.desktop-hover-trigger');
    allTriggers.forEach(t => {
      if (t !== trigger) {
        t.classList.remove('active');
      }
    });

    // Show current submenu
    trigger.classList.add('active');
  }

  hideDesktopSubmenu(trigger) {
    // Small delay to prevent flickering
    setTimeout(() => {
      trigger.classList.remove('active');
    }, 100);
  }

  handleSearch() {
    // Implement search functionality
    const searchInput = prompt('Search for products...');
    if (searchInput && searchInput.trim()) {
      const searchUrl = `/search?type=product&q=${encodeURIComponent(searchInput.trim())}`;
      window.location.href = searchUrl;
    }
  }

  setupGATracking() {
    // Track menu interactions
    const menuItems = document.querySelectorAll('.desktop-ga-menu-product, .mobile-ga-menu-product');
    menuItems.forEach(item => {
      item.addEventListener('click', (e) => {
        const menuText = item.textContent.trim();
        const href = item.getAttribute('href');
        
        // GA tracking
        if (typeof gtag !== 'undefined') {
          gtag('event', 'menu_click', {
            'menu_item': menuText,
            'menu_url': href,
            'device_type': this.isMobile ? 'mobile' : 'desktop'
          });
        }
      });
    });

    // Track hamburger menu open
    if (this.hamburgerBtn) {
      this.hamburgerBtn.addEventListener('click', () => {
        if (typeof gtag !== 'undefined') {
          gtag('event', 'mobile_menu_open', {
            'device_type': 'mobile'
          });
        }
      });
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new NewHeaderNavigation();
});

// Export for potential external use
window.NewHeaderNavigation = NewHeaderNavigation;
